import type { Flashcard } from '@/types/Flashcard';
import { getString } from '@/utils/chatUtils/chatDefaultUtils';
import { getAnswerOptionLetter } from '@/utils/vignetteAnswerOption';
import type { ServedQuestionMarks } from './useChatQuestionMarks';

/**
 * The stored marks the payload carries (ORA-822), when it carries them. A
 * block served before they existed has neither field, and hydrates elsewhere.
 */
export const readServedMarks = (
  item?: Record<string, unknown>
): ServedQuestionMarks | undefined => {
  const hasHighlights = item !== undefined && 'question_highlights' in item;
  const crossouts = item?.answer_crossouts;

  if (!hasHighlights && !Array.isArray(crossouts)) return undefined;

  return {
    questionHighlights: getString(item?.question_highlights) || null,
    answerCrossouts: Array.isArray(crossouts)
      ? crossouts.map((crossedOut) => crossedOut === true)
      : [],
  };
};

export type ResolvedOption = {
  letter: string;
  text: string;
  explanation: string;
  isCorrect: boolean;
};

// Resolve each option's letter once — falling back to its position the way QBank
// does, since the field is not always populated — so the buttons, the
// explanation list, the React keys and the correctness gate can never disagree
// about which option is which. `correct_letter` wins when the payload has one;
// each option's `is_correct` is only consulted when it doesn't. Shared by the
// render and the linked-flashcard fetch effect (which runs before the component's
// early return, so it can't read the render-scoped list).
export const resolveQuestionOptions = (
  item?: Record<string, unknown>
): ResolvedOption[] => {
  const rawOptions = Array.isArray(item?.options)
    ? (item.options as Array<Record<string, unknown>>)
    : [];

  const correctLetter = getString(item?.correct_letter);

  return rawOptions.map((option, index) => {
    const letter = getAnswerOptionLetter(
      { letter: getString(option.letter) || undefined },
      index
    );

    return {
      letter,
      text: getString(option.text),
      explanation: getString(option.explanation_main),
      isCorrect: correctLetter
        ? letter === correctLetter
        : option.is_correct === true,
    };
  });
};

export const isAnsweredCorrectly = (
  options: ResolvedOption[],
  letter: string
) =>
  Boolean(letter) &&
  options.some((option) => option.letter === letter && option.isCorrect);

// Map a fetched linked Flashcard into the shape FlashcardsBlockBody renders. The
// first (canonical) variant carries the display copy; fall back to the flat
// front/back for legacy shapes.
export const toFlashcardToolItem = (
  flashcard: Flashcard
): Record<string, unknown> => {
  const variant = flashcard.variants?.[0];

  return {
    id: flashcard.id,
    front: variant?.front ?? flashcard.front ?? '',
    back: variant?.back ?? flashcard.back ?? '',
    explanation_short: variant?.explanationShort ?? '',
  };
};
